import { type ReactElement, type ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import { Button, type ButtonTone } from "./Button";

export type ToastVariant = "neutral" | "success" | "alert" | "warning";

export interface ToastProps {
  message: string;
  description?: string;
  variant?: ToastVariant;
  icon: ReactElement<{ size?: number | string }>;
  action?: {
    label: string;
    onClick: () => void;
  };
  "data-testid"?: string;
}

const ACTION_TONE_BY_VARIANT: Record<ToastVariant, ButtonTone> = {
  neutral: "primary",
  success: "success",
  alert: "error",
  warning: "warning",
};

export function Toast({
  message,
  description,
  variant = "neutral",
  icon,
  action,
  "data-testid": dataTestId,
}: ToastProps): ReactNode {
  return (
    <div
      aria-atomic="true"
      aria-live={variant === "alert" ? "assertive" : "polite"}
      className={twJoin(
        "flex min-h-toast-height max-w-toast items-center gap-sm rounded-toast border px-toast-inline py-toast-block",
        variant === "alert" && "border-border-on-error bg-feedback-error text-text-on-danger-secondary",
        variant === "neutral" && "border-border-on-primary bg-feedback-primary text-text-on-primary",
        variant === "success" && "border-border-on-success bg-feedback-success text-text-on-success-secondary",
        variant === "warning" && "border-border-on-warning bg-feedback-warning text-text-on-warning-secondary",
      )}
      data-testid={dataTestId}
    >
      <div className="flex min-w-shrinkable flex-1 items-center gap-sm">
        <div className="shrink-0">{icon}</div>
        <div className="flex min-w-shrinkable flex-col">
          <span className={description ? "text-caption-emphasis" : "text-caption"}>{message}</span>
          {description && <span className="block text-caption whitespace-pre-wrap">{description}</span>}
        </div>
      </div>
      {action && (
        <Button
          type="button"
          variant="subtle"
          tone={ACTION_TONE_BY_VARIANT[variant]}
          size="sm"
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}

export function ToastViewport({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="pointer-events-none fixed top-space-md left-1/2 z-toast mx-auto flex max-w-full -translate-x-1/2 flex-col items-center gap-sm">
      {children}
    </div>
  );
}
